import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { PanelExpandableComponent } from '@app/shared/ux/panel-expandable/panel-expandable.component';
import { CoverageIdEnum, NewModal } from 'outshared-lib';
import { CoverageGroupDefault, CoverageInput } from '../../classes';
import { CoverageGroupLayoutEnum } from '../../enums';
import { CoverageInputDataInputInterface } from '@app/my-zone/shared/insurance-coverage/insurance-coverage-control';
import { WucButtonModule, WucCardModule, WucLinkModule, WucPriceModule } from '@inshared/website-ui-components';
import { TooltipModule } from '@app/shared/tooltip/tooltip.module';
import { NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'ins-coverage-card',
    templateUrl: 'coverage-card.component.html',
    styleUrls: ['./coverage-card.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CoverageCardComponent),
        multi: true,
    }],
    standalone: true,
    imports: [
        WucCardModule,
        TooltipModule,
        WucPriceModule,
        WucButtonModule,
        WucLinkModule,
        NgFor,
        NgIf
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageCardComponent implements ControlValueAccessor {

    @Input() public group!: CoverageGroupDefault;

    public selectedInputs: { [coverageId: string]: CoverageInput } = {};
    public coverageGroupLayoutEnum: typeof CoverageGroupLayoutEnum;
    public propagateChange;
    public description: string;
    public coverageIdEnum: typeof CoverageIdEnum = CoverageIdEnum;

    constructor(
        private newModal: NewModal,
    ) {
        this.coverageGroupLayoutEnum = CoverageGroupLayoutEnum;
    }

    public changeValue(): void {
        this.propagateChange();
    }

    public writeValue(value: CoverageIdEnum[]): void {
        if (value && value.length > 0) {
            const foundCoverage = this.findSelectedCoverage(value);
            this.selectedInputs = { [foundCoverage.id]: foundCoverage };
        }
    }

    public registerOnChange(fn: any): void {
        this.propagateChange = () => {
            fn(Object.values(this.selectedInputs)
                .map((input) => input?.coverageIds)
                .flat(1));
        };
    }

    public registerOnTouched(): void {
        return;
    }

    public async displayInformation(data: CoverageInputDataInputInterface): Promise<void> {
        const { CoverageControlInformationModal } = await import('../coverage-control-information/coverage-control-information.modal');
        this.newModal.open(CoverageControlInformationModal, { data });
    }

    public togglePanel(event: Event, panel: PanelExpandableComponent): void {
        event.stopPropagation();
        event.preventDefault();
        panel.toggle();
    }

    private findSelectedCoverage(coverageIds: CoverageIdEnum[]): CoverageInput {
        const foundInput = this.group.inputs
            .find((input) => this.compareArrays(input.coverageIds, coverageIds));

        if (!foundInput) {
            return null;
        }

        return foundInput;
    }

    private compareArrays(array1: any[], array2: any[]): boolean {
        if (array1.length !== array2.length) {
            return false;
        }

        const a = array1.sort();
        const b = array2.sort();
        return a.every((element, index) => element === b[index]);
    }

    public selectCoverage(input: CoverageInput) {
        if (!input.acceptance) {
            return;
        }

        this.selectedInputs = { ...((!this.group.single) ? this.selectedInputs : {}), [input.id]: input };
        this.propagateChange();
    }

    public deselectCoverage(input: CoverageInput) {
        if (this.group.single) {
            return;
        }

        delete this.selectedInputs[input.id];
        this.propagateChange();
    }
}
